import { useState, useEffect } from 'react';
import { usePublicClient } from 'wagmi';
import { formatUnits } from 'viem';

const USDG_ADDRESS = '0xe343167631d89B6Ffc58B88d6b7fB0228795491D' as const;
const INK_CHAIN_ID = 57073;

const USDG_ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'decimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint8' }],
  },
] as const;

export function useUsdgBalance(address: `0x${string}` | undefined) {
  const [balance, setBalance] = useState('0.00');
  const [balanceUSD, setBalanceUSD] = useState('0.00');
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const publicClient = usePublicClient({ chainId: INK_CHAIN_ID });

  const fetchBalance = async () => {
    if (!address || !publicClient) {
      setBalance('0.00');
      setBalanceUSD('0.00');
      return;
    }

    try {
      const balanceWei = await publicClient.readContract({
        address: USDG_ADDRESS,
        abi: USDG_ABI,
        functionName: 'balanceOf',
        args: [address],
      });

      const decimals = await publicClient.readContract({
        address: USDG_ADDRESS,
        abi: USDG_ABI,
        functionName: 'decimals',
      });

      const formattedBalance = formatUnits(balanceWei as bigint, decimals);
      const balanceNum = parseFloat(formattedBalance);
      
      setBalance(balanceNum.toFixed(2));
      setBalanceUSD(balanceNum.toFixed(2)); // 1:1 with USD
    } catch (error) {
      console.error('Failed to fetch USDG balance:', error);
      setBalance('0.00');
      setBalanceUSD('0.00');
    }
  };

  useEffect(() => {
    fetchBalance();
    
    // Refresh balance every 10 seconds
    const interval = setInterval(fetchBalance, 10000);
    return () => clearInterval(interval);
  }, [address, publicClient, refetchTrigger]);

  const refetch = () => {
    setRefetchTrigger(prev => prev + 1);
  };

  return { balance, balanceUSD, refetch };
}

